/**
 * The game's "Home" control on core's auth screens (login, register,
 * forgot, reset, verify, check-email): core screens cannot be edited, so
 * main.ts lays this button over them. Plain DOM (no Vue) so the auth pages
 * stay light. Top left, 44px tall, inside the safe area; goes to the
 * landing page.
 */
// The strings load on demand (only on the auth pages): a static import here
// would pull vue-i18n, the whole English table and the consent store into
// the entry chunk that every page, the landing included, waits for.

const ID = 'tkd-auth-home';
export const HOME_ICON = '/art/ui/icon96/ui_icon_home.webp';

export const AUTH_PATHS = new Set(['/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/check-email']);

export function isAuthRoute(pathname: string): boolean {
  return AUTH_PATHS.has(pathname);
}

/** Shows (or hides) the Home button; `go` navigates without a reload. */
export function syncAuthHome(pathname: string, go: (to: string) => void): void {
  const existing = document.getElementById(ID);
  if (!isAuthRoute(pathname)) {
    existing?.remove();
    return;
  }
  if (existing) return;
  const a = document.createElement('a');
  a.id = ID;
  a.href = '/';
  // The brand name until the localised label lands (a moment later, see below).
  a.setAttribute('aria-label', 'Tankodromo');
  a.dataset.authHome = '';
  a.className =
    'group bevel fixed z-50 flex min-h-11 min-w-11 items-center gap-2 rounded-xl border-[3px] border-ink bg-gunmetal px-2 py-1 ' +
    'font-display text-sm text-sand-100 uppercase no-underline shadow-press transition-transform hover:no-underline ' +
    'active:translate-y-[3px] active:shadow-press-down focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-sand-100 ' +
    'top-[max(0.75rem,env(safe-area-inset-top))] left-[max(0.75rem,env(safe-area-inset-left))]';
  a.innerHTML =
    // Painted home icon (owner 2026-10-05: "generate a game-style icon for the auth Home button").
    `<img src="${HOME_ICON}" alt="" aria-hidden="true" width="30" height="30" class="size-[30px] shrink-0 object-contain drop-shadow-[0_2px_0_var(--color-ink)] transition-transform motion-safe:group-hover:-translate-y-0.5 motion-safe:group-hover:scale-110" draggable="false" data-auth-home-icon />` +
    '<img src="/art/brand/logo_tankodromo_640.png" alt="" aria-hidden="true" class="hidden h-7 w-auto sm:block" draggable="false" />' +
    '<span class="sm:hidden" data-auth-home-text></span>';
  // English first, then the player's language once its table is here (a missing key stays English).
  void Promise.all([import('./i18n/locales/en'), import('./i18n'), import('./i18n/locale-pref')])
    .then(async ([{ default: en }, { localeMessages }, { readLocale }]) => {
      a.setAttribute('aria-label', en.nav.authHome);
      a.querySelector('[data-auth-home-text]')!.textContent = en.nav.authHomeShort;
      const m = await localeMessages(readLocale());
      if (m.nav?.authHome) a.setAttribute('aria-label', m.nav.authHome);
      if (m.nav?.authHomeShort) a.querySelector('[data-auth-home-text]')!.textContent = m.nav.authHomeShort;
    })
    .catch(() => undefined);
  a.addEventListener('click', (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    go('/');
  });
  document.body.appendChild(a);
}
